import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import axios from 'axios';
import {
  Zap,
  Mail,
  FileText,
  Activity,
  Settings,
  Play,
  Pause,
  Plus,
  Send,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Eye,
  Edit3,
  Trash2,
  Filter,
  Search,
  ChevronDown,
  ChevronRight,
  Key,
  Shield,
  Users,
  Building2,
  Ticket,
  Bell,
  CreditCard,
  AlertCircle,
  Info,
  Code,
  Copy,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Badge } from '../../../../components/ui/badge';
import { Switch } from '../../../../components/ui/switch';
import { Label } from '../../../../components/ui/label';
import { Textarea } from '../../../../components/ui/textarea';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// ============ STATUS BADGES ============

const WorkflowStatusBadge = ({ status }) => {
  const config = {
    active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Active' },
    paused: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Paused' },
    draft: { bg: 'bg-zinc-600/50', text: 'text-zinc-400', label: 'Draft' },
  };
  const c = config[status] || config.draft;
  return (
    <Badge className={`${c.bg} ${c.text} border-0`}>{c.label}</Badge>
  );
};

const DeliveryStatusBadge = ({ status }) => {
  const config = {
    sent: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Sent' },
    delivered: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Delivered' },
    failed: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Failed' },
    pending: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Pending' },
    bounced: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Bounced' },
  };
  const c = config[status] || config.pending;
  return (
    <Badge className={`${c.bg} ${c.text} border-0`}>{c.label}</Badge>
  );
};

const CategoryIcon = ({ category }) => {
  const icons = {
    authentication: Key,
    agency_lifecycle: Building2,
    client_self_repair: Users,
    support_tickets: Ticket,
    billing: CreditCard,
    system_alerts: AlertTriangle,
  };
  const Icon = icons[category] || Zap;
  return <Icon className="w-4 h-4" />;
};

const SenderScopeBadge = ({ scope, isFallback }) => {
  if (scope === 'platform') {
    return (
      <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
        Platform Mailgun
      </span>
    );
  }
  if (scope === 'agency' && isFallback) {
    return (
      <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">
        Platform Fallback
      </span>
    );
  }
  return (
    <span className="text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded">
      Agency Mailgun
    </span>
  );
};

// ============ WORKFLOWS TAB ============

const WorkflowsTab = ({ token, onRefresh }) => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);

  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/platform/system-automations/workflows?limit=100`;
      if (categoryFilter !== 'all') url += `&category=${categoryFilter}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkflows(res.data.workflows || []);
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
      toast.error('Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [token, categoryFilter, statusFilter]);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  const handleActivate = async (workflowId) => {
    try {
      await axios.post(`${API}/platform/system-automations/workflows/${workflowId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Workflow activated');
      fetchWorkflows();
    } catch (err) {
      toast.error('Failed to activate workflow');
    }
  };

  const handlePause = async (workflowId) => {
    try {
      await axios.post(`${API}/platform/system-automations/workflows/${workflowId}/pause`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Workflow paused');
      fetchWorkflows();
    } catch (err) {
      toast.error('Failed to pause workflow');
    }
  };

  const handleSendTest = async () => {
    if (!testEmail || !selectedWorkflow) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/platform/system-automations/workflows/${selectedWorkflow.id}/test?recipient_email=${encodeURIComponent(testEmail)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success('Test email sent successfully');
        setTestModalOpen(false);
      } else {
        toast.error(res.data.error || 'Failed to send test email');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'authentication', label: 'Authentication' },
    { value: 'agency_lifecycle', label: 'Agency Lifecycle' },
    { value: 'client_self_repair', label: 'Client / Self-Repair' },
    { value: 'support_tickets', label: 'Support / Tickets' },
    { value: 'billing', label: 'Billing' },
    { value: 'system_alerts', label: 'System Alerts' },
  ];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700 text-white">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            {categories.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 bg-zinc-800 border-zinc-700 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
          </SelectContent>
        </Select>
        
        <Button
          variant="outline"
          size="sm"
          onClick={fetchWorkflows}
          className="border-zinc-700 text-zinc-300"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Workflows Table */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : workflows.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12 text-center">
            <Zap className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No workflows found</p>
            <p className="text-sm text-zinc-500 mt-1">Seed default workflows to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border border-zinc-800 overflow-hidden">
          <Table>
            <TableHeader className="bg-zinc-900/80">
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400">Workflow</TableHead>
                <TableHead className="text-zinc-400">Category</TableHead>
                <TableHead className="text-zinc-400">Trigger</TableHead>
                <TableHead className="text-zinc-400">Sender</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
                <TableHead className="text-zinc-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {workflows.map((workflow) => (
                <TableRow key={workflow.id} className="border-zinc-800 hover:bg-zinc-800/50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        workflow.status === 'active' ? 'bg-emerald-500/20' :
                        workflow.status === 'draft' ? 'bg-zinc-600/30' : 'bg-amber-500/20'
                      }`}>
                        <CategoryIcon category={workflow.category} />
                      </div>
                      <div>
                        <p className="font-medium text-white">{workflow.name}</p>
                        <p className="text-xs text-zinc-500">{workflow.description}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-zinc-300 capitalize">
                      {workflow.category?.replace(/_/g, ' ')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <code className="text-xs text-zinc-400 bg-zinc-800 px-2 py-1 rounded">
                      {workflow.trigger_event}
                    </code>
                  </TableCell>
                  <TableCell>
                    <SenderScopeBadge scope={workflow.sender_scope} />
                  </TableCell>
                  <TableCell>
                    <WorkflowStatusBadge status={workflow.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedWorkflow(workflow);
                          setTestModalOpen(true);
                        }}
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                        title="Send Test"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                      {workflow.status === 'active' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handlePause(workflow.id)}
                          className="h-8 w-8 p-0 text-amber-400 hover:text-amber-300"
                          title="Pause"
                        >
                          <Pause className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleActivate(workflow.id)}
                          className="h-8 w-8 p-0 text-emerald-400 hover:text-emerald-300"
                          title="Activate"
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Test Email Modal */}
      <Dialog open={testModalOpen} onOpenChange={setTestModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-500" />
              Test Workflow: {selectedWorkflow?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Send a test email to verify this workflow is working correctly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Recipient Email</Label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-xs text-zinc-500 mb-1">Trigger Event</p>
              <code className="text-sm text-emerald-400">{selectedWorkflow?.trigger_event}</code>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestModalOpen(false)} className="border-zinc-600 text-zinc-300">
              Cancel
            </Button>
            <Button
              onClick={handleSendTest}
              disabled={sending || !testEmail}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ TEMPLATES TAB ============

const TemplatesTab = ({ token }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/platform/system-automations/templates?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handlePreview = async (template) => {
    try {
      const res = await axios.post(
        `${API}/platform/system-automations/templates/${template.id}/preview`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewHtml(res.data.body_html);
      setPreviewOpen(true);
    } catch (err) {
      toast.error('Failed to preview template');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-400">
          Manage reusable email templates for system workflows.
        </p>
        <Button
          onClick={() => {
            setSelectedTemplate(null);
            setEditorOpen(true);
          }}
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : templates.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No templates created yet</p>
            <p className="text-sm text-zinc-500 mt-1">Create templates to use in system workflows</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((template) => (
            <Card key={template.id} className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-white text-base">{template.name}</CardTitle>
                    <CardDescription className="text-zinc-400 text-sm">
                      {template.template_type?.replace(/_/g, ' ')}
                    </CardDescription>
                  </div>
                  <WorkflowStatusBadge status={template.status} />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-xs text-zinc-500 uppercase mb-1">Subject</p>
                  <p className="text-sm text-zinc-300 truncate">{template.subject}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePreview(template)}
                    className="flex-1 border-zinc-700 text-zinc-300"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedTemplate(template);
                      setEditorOpen(true);
                    }}
                    className="flex-1 border-zinc-700 text-zinc-300"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
          </DialogHeader>
          <div className="bg-white rounded-lg p-4 overflow-auto max-h-[60vh]">
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Editor Modal */}
      <TemplateEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        template={selectedTemplate}
        token={token}
        onSaved={fetchTemplates}
      />
    </div>
  );
};

// ============ TEMPLATE EDITOR MODAL ============

const TemplateEditorModal = ({ isOpen, onClose, template, token, onSaved }) => {
  const [form, setForm] = useState({
    name: '',
    template_type: 'password_reset',
    subject: '',
    preview_text: '',
    body_html: '',
    body_text: '',
    sender_scope: 'platform',
  });
  const [saving, setSaving] = useState(false);
  const [variables, setVariables] = useState([]);

  useEffect(() => {
    if (template) {
      setForm({
        name: template.name || '',
        template_type: template.template_type || 'password_reset',
        subject: template.subject || '',
        preview_text: template.preview_text || '',
        body_html: template.body_html || '',
        body_text: template.body_text || '',
        sender_scope: template.sender_scope || 'platform',
      });
    } else {
      setForm({
        name: '',
        template_type: 'password_reset',
        subject: '',
        preview_text: '',
        body_html: '',
        body_text: '',
        sender_scope: 'platform',
      });
    }
  }, [template, isOpen]);

  useEffect(() => {
    // Fetch available variables
    const fetchVars = async () => {
      try {
        const res = await axios.get(`${API}/platform/system-automations/variables`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVariables(res.data.variables || {});
      } catch (err) {
        console.error('Failed to fetch variables:', err);
      }
    };
    if (isOpen) fetchVars();
  }, [token, isOpen]);

  const handleSave = async () => {
    if (!form.name || !form.subject || !form.body_html) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      if (template?.id) {
        await axios.put(`${API}/platform/system-automations/templates/${template.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/platform/system-automations/templates`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Template created');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (varName) => {
    const placeholder = `{{${varName}}}`;
    setForm({ ...form, body_html: form.body_html + placeholder });
  };

  const templateTypes = [
    { value: 'password_reset', label: 'Password Reset' },
    { value: 'email_confirmation', label: 'Email Confirmation' },
    { value: 'agency_owner_welcome', label: 'Agency Owner Welcome' },
    { value: 'agency_admin_invite', label: 'Agency Admin Invite' },
    { value: 'self_repair_signup', label: 'Self-Repair Signup' },
    { value: 'client_portal_welcome', label: 'Client Portal Welcome' },
    { value: 'client_password_reset', label: 'Client Password Reset' },
    { value: 'ticket_received', label: 'Ticket Received' },
    { value: 'ticket_reply_received', label: 'Ticket Reply' },
    { value: 'ticket_status_updated', label: 'Ticket Status Updated' },
    { value: 'ticket_escalated', label: 'Ticket Escalated' },
    { value: 'billing_alert', label: 'Billing Alert' },
    { value: 'security_alert', label: 'Security Alert' },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-500" />
            {template ? 'Edit Template' : 'Create Template'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Template Name *</Label>
              <Input
                placeholder="Password Reset Email"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Template Type *</Label>
              <Select value={form.template_type} onValueChange={(v) => setForm({ ...form, template_type: v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {templateTypes.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400">Subject *</Label>
            <Input
              placeholder="Reset Your Password"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400">Preview Text</Label>
            <Input
              placeholder="Click to reset your password..."
              value={form.preview_text}
              onChange={(e) => setForm({ ...form, preview_text: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-zinc-400">Body HTML *</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-500">Insert Variable:</span>
                <Select onValueChange={insertVariable}>
                  <SelectTrigger className="w-40 h-7 text-xs bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700 max-h-60">
                    {Object.entries(variables).map(([group, vars]) => (
                      <div key={group}>
                        <p className="px-2 py-1 text-xs text-zinc-500 uppercase">{group}</p>
                        {vars.map(v => (
                          <SelectItem key={v.name} value={v.name}>
                            {`{{${v.name}}}`}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Textarea
              placeholder="<div>Hello {{first_name}},</div>"
              value={form.body_html}
              onChange={(e) => setForm({ ...form, body_html: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm min-h-[200px]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400">Body Text (fallback)</Label>
            <Textarea
              placeholder="Plain text version for email clients that don't support HTML"
              value={form.body_text}
              onChange={(e) => setForm({ ...form, body_text: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white min-h-[80px]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400">Sender Scope</Label>
            <Select value={form.sender_scope} onValueChange={(v) => setForm({ ...form, sender_scope: v })}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="platform">Platform Mailgun</SelectItem>
                <SelectItem value="agency">Agency Mailgun</SelectItem>
                <SelectItem value="agency_with_fallback">Agency with Platform Fallback</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
          >
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
            {template ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ TRIGGERS TAB ============

const TriggersTab = ({ token }) => {
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTriggers = async () => {
      try {
        const res = await axios.get(`${API}/platform/system-automations/triggers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTriggers(res.data.triggers || []);
      } catch (err) {
        console.error('Failed to fetch triggers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTriggers();
  }, [token]);

  const groupedTriggers = triggers.reduce((acc, trigger) => {
    const cat = trigger.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(trigger);
    return acc;
  }, {});

  const categoryLabels = {
    authentication: 'Authentication',
    agency_lifecycle: 'Agency Lifecycle',
    client_self_repair: 'Client / Self-Repair',
    support_tickets: 'Support / Tickets',
    system: 'System',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-zinc-400">
        Available system triggers that can fire email workflows.
      </p>

      {Object.entries(groupedTriggers).map(([category, catTriggers]) => (
        <Card key={category} className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CategoryIcon category={category} />
              <CardTitle className="text-white text-base">
                {categoryLabels[category] || category}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {catTriggers.map((trigger) => (
                <div
                  key={trigger.event}
                  className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                >
                  <div className="flex-1">
                    <code className="text-sm text-emerald-400">{trigger.event}</code>
                    <p className="text-xs text-zinc-500 mt-0.5">{trigger.description}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {trigger.workflow_name ? (
                      <span className="text-xs text-zinc-300">
                        → {trigger.workflow_name}
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-500">No workflow assigned</span>
                    )}
                    <div className={`w-2 h-2 rounded-full ${trigger.enabled ? 'bg-emerald-500' : 'bg-zinc-600'}`} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ============ DELIVERY LOGS TAB ============

const DeliveryLogsTab = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    email_type: 'all',
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/platform/system-automations/delivery-logs?limit=50`;
      if (filters.status && filters.status !== 'all') url += `&status=${filters.status}`;
      if (filters.email_type && filters.email_type !== 'all') url += `&email_type=${filters.email_type}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="w-36 bg-zinc-800 border-zinc-700 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="bounced">Bounced</SelectItem>
          </SelectContent>
        </Select>
        
        <Button
          variant="outline"
          size="sm"
          onClick={fetchLogs}
          className="border-zinc-700 text-zinc-300"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : logs.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12 text-center">
            <Activity className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No delivery logs yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border border-zinc-800 overflow-hidden">
          <Table>
            <TableHeader className="bg-zinc-900/80">
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400">Timestamp</TableHead>
                <TableHead className="text-zinc-400">Recipient</TableHead>
                <TableHead className="text-zinc-400">Type</TableHead>
                <TableHead className="text-zinc-400">Provider</TableHead>
                <TableHead className="text-zinc-400">Status</TableHead>
                <TableHead className="text-zinc-400">Message ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} className="border-zinc-800 hover:bg-zinc-800/50">
                  <TableCell className="text-zinc-400 text-sm">
                    {log.created_at ? new Date(log.created_at).toLocaleString() : '-'}
                  </TableCell>
                  <TableCell className="text-white">{log.recipient_email}</TableCell>
                  <TableCell>
                    <span className="text-sm text-zinc-300">
                      {log.email_type?.replace(/_/g, ' ')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <SenderScopeBadge scope={log.sender_scope} isFallback={log.is_fallback} />
                  </TableCell>
                  <TableCell>
                    <DeliveryStatusBadge status={log.status} />
                  </TableCell>
                  <TableCell>
                    {log.mailgun_message_id ? (
                      <code className="text-xs text-zinc-500 truncate max-w-[150px] block">
                        {log.mailgun_message_id}
                      </code>
                    ) : (
                      <span className="text-zinc-600">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

// ============ SETTINGS TAB ============

const SettingsTab = ({ token }) => {
  const [settings, setSettings] = useState({
    enable_system_automations: true,
    enable_password_reset_emails: true,
    enable_email_confirmation_emails: true,
    enable_agency_onboarding_emails: true,
    enable_ticket_notification_emails: true,
    enable_fallback_sender_for_agency: true,
    default_retry_attempts: 3,
    token_expiration_minutes: 60,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/platform/system-automations/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSettings(res.data);
      } catch (err) {
        console.error('Failed to fetch settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/platform/system-automations/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Global Settings</CardTitle>
          <CardDescription className="text-zinc-400">
            Configure system-wide automation settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
            <div>
              <Label className="text-white">Enable System Automations</Label>
              <p className="text-xs text-zinc-400">Master switch for all system email automations</p>
            </div>
            <Switch
              checked={settings.enable_system_automations}
              onCheckedChange={(v) => setSettings({ ...settings, enable_system_automations: v })}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
            <div>
              <Label className="text-white">Password Reset Emails</Label>
              <p className="text-xs text-zinc-400">Send password reset emails when requested</p>
            </div>
            <Switch
              checked={settings.enable_password_reset_emails}
              onCheckedChange={(v) => setSettings({ ...settings, enable_password_reset_emails: v })}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
            <div>
              <Label className="text-white">Email Confirmation Emails</Label>
              <p className="text-xs text-zinc-400">Send email verification links</p>
            </div>
            <Switch
              checked={settings.enable_email_confirmation_emails}
              onCheckedChange={(v) => setSettings({ ...settings, enable_email_confirmation_emails: v })}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
            <div>
              <Label className="text-white">Agency Onboarding Emails</Label>
              <p className="text-xs text-zinc-400">Send welcome and invite emails for agencies</p>
            </div>
            <Switch
              checked={settings.enable_agency_onboarding_emails}
              onCheckedChange={(v) => setSettings({ ...settings, enable_agency_onboarding_emails: v })}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
            <div>
              <Label className="text-white">Ticket Notification Emails</Label>
              <p className="text-xs text-zinc-400">Send ticket created, replied, and status update emails</p>
            </div>
            <Switch
              checked={settings.enable_ticket_notification_emails}
              onCheckedChange={(v) => setSettings({ ...settings, enable_ticket_notification_emails: v })}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
            <div>
              <Label className="text-white">Platform Fallback for Agency Emails</Label>
              <p className="text-xs text-zinc-400">Use Platform Mailgun when Agency Mailgun is unavailable</p>
            </div>
            <Switch
              checked={settings.enable_fallback_sender_for_agency}
              onCheckedChange={(v) => setSettings({ ...settings, enable_fallback_sender_for_agency: v })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Default Retry Attempts</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={settings.default_retry_attempts}
                onChange={(e) => setSettings({ ...settings, default_retry_attempts: parseInt(e.target.value) || 3 })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Token Expiration (minutes)</Label>
              <Input
                type="number"
                min="15"
                max="1440"
                value={settings.token_expiration_minutes}
                onChange={(e) => setSettings({ ...settings, token_expiration_minutes: parseInt(e.target.value) || 60 })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
        >
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
          Save Settings
        </Button>
      </div>
    </div>
  );
};

// ============ MAIN PAGE ============

const SystemAutomationsPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/platform/system-automations/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchStats();
  }, [token, fetchStats]);

  const handleSeedWorkflows = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/platform/system-automations/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchStats();
    } catch (err) {
      toast.error('Failed to seed workflows');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="text-white p-6 lg:p-8 space-y-6" data-testid="system-automations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-emerald-500/20 rounded-xl">
            <Zap className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">System Automations</h1>
            <p className="text-sm text-zinc-400">
              Manage platform-wide transactional email workflows
            </p>
          </div>
        </div>
        <Button
          onClick={handleSeedWorkflows}
          disabled={seeding}
          variant="outline"
          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
        >
          {seeding ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          Seed Default Workflows
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Active Workflows</p>
                  <p className="text-2xl font-bold text-emerald-400">{stats.workflows?.active || 0}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-emerald-500/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Draft Workflows</p>
                  <p className="text-2xl font-bold text-zinc-400">{stats.workflows?.draft || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-zinc-500/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Emails Sent (24h)</p>
                  <p className="text-2xl font-bold text-white">{stats.delivery_24h?.sent || 0}</p>
                </div>
                <Mail className="w-8 h-8 text-emerald-500/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Failed (24h)</p>
                  <p className="text-2xl font-bold text-red-400">{stats.delivery_24h?.failed || 0}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500/30" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="workflows" className="space-y-4">
        <TabsList className="bg-zinc-900/50 border border-zinc-800 p-1">
          <TabsTrigger value="workflows" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
            <Zap className="w-4 h-4 mr-2" />
            Workflows
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
            <FileText className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="triggers" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
            <Bell className="w-4 h-4 mr-2" />
            Triggers
          </TabsTrigger>
          <TabsTrigger value="logs" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
            <Activity className="w-4 h-4 mr-2" />
            Delivery Logs
          </TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="workflows">
          <WorkflowsTab token={token} onRefresh={fetchStats} />
        </TabsContent>
        <TabsContent value="templates">
          <TemplatesTab token={token} />
        </TabsContent>
        <TabsContent value="triggers">
          <TriggersTab token={token} />
        </TabsContent>
        <TabsContent value="logs">
          <DeliveryLogsTab token={token} />
        </TabsContent>
        <TabsContent value="settings">
          <SettingsTab token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SystemAutomationsPage;
